import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  Heart,
  MessageCircle,
  Send,
  Image as ImageIcon,
  X,
  Trash2,
  TrendingUp,
  HelpCircle,
  Trophy,
  Loader2,
  ArrowLeft
} from 'lucide-react';

const API = '/api';

const POST_TYPES = [
  { id: 'progress', label: 'İlerleme', icon: TrendingUp, color: 'bg-green-100 text-green-700' },
  { id: 'question', label: 'Soru', icon: HelpCircle, color: 'bg-blue-100 text-blue-700' },
  { id: 'win', label: 'Başarı', icon: Trophy, color: 'bg-yellow-100 text-yellow-700' },
];

const CommunityPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [newPost, setNewPost] = useState('');
  const [postType, setPostType] = useState('progress');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});
  const fileInputRef = useRef(null);
  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}')?.id;

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/community/posts`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Görsel 2MB\'dan küçük olmalı');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target.result);
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePost = async () => {
    if (!newPost.trim()) {
      toast.error('Bir şeyler yazın');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Lütfen giriş yapın');
      return;
    }

    setPosting(true);
    try {
      const response = await axios.post(
        `${API}/community/posts`,
        {
          content: newPost,
          post_type: postType,
          image_base64: selectedImage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts([response.data, ...posts]);
      setNewPost('');
      setSelectedImage(null);
      setImagePreview(null);
      toast.success('Paylaşıldı! 🎉');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Paylaşım başarısız');
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Lütfen giriş yapın');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/community/posts/${postId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, like_count: response.data.like_count, likes: response.data.action === 'liked' ? [...(p.likes || []), currentUserId] : (p.likes || []).filter(id => id !== currentUserId) }
          : p
      ));
    } catch (error) {
      toast.error('Beğeni başarısız');
    }
  };

  const handleComment = async (postId) => {
    const text = commentText[postId];
    if (!text?.trim()) return;

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Lütfen giriş yapın');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/community/posts/${postId}/comments`,
        { content: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, comments: [...(p.comments || []), response.data], comment_count: (p.comment_count || 0) + 1 }
          : p
      ));
      setCommentText({ ...commentText, [postId]: '' });
    } catch (error) {
      toast.error('Yorum başarısız');
    }
  };

  const handleDelete = async (postId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post silindi');
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const getPostTypeInfo = (type) => POST_TYPES.find(t => t.id === type) || POST_TYPES[0];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return 'Az önce';
    if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
    return date.toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-4 text-purple-600 hover:text-purple-800"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri
        </Button>
        
        <h1 className="text-3xl font-bold mb-6">Topluluk 🏠</h1>

        {/* New Post Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            {/* Post Type Selector */}
            <div className="flex gap-2 mb-4">
              {POST_TYPES.map(type => (
                <button
                  key={type.id}
                  onClick={() => setPostType(type.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all ${
                    postType === type.id ? type.color : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <type.icon className="h-4 w-4" />
                  {type.label}
                </button>
              ))}
            </div>

            <Textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder="Ne düşünüyorsun? İlerlemenizi paylaşın, soru sorun veya başarınızı kutlayın! 🎯"
              className="min-h-[100px] resize-none"
            />

            {imagePreview && (
              <div className="relative mt-3">
                <img src={imagePreview} alt="Preview" className="max-h-48 rounded-lg" />
                <button
                  onClick={() => { setSelectedImage(null); setImagePreview(null); }}
                  className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-full"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="flex justify-between items-center mt-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                accept="image/*"
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Fotoğraf
              </Button>
              <Button onClick={handlePost} disabled={posting || !newPost.trim()}>
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Paylaş
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Empty State */}
        {posts.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-gray-500 text-lg">Toplulukta ilk paylaşımı sen yap! 🚀</p>
            </CardContent>
          </Card>
        )}

        {/* Posts Feed */}
        <div className="space-y-4">
          {posts.map(post => {
            const typeInfo = getPostTypeInfo(post.post_type);
            const isLiked = (post.likes || []).includes(currentUserId);

            return (
              <Card key={post.id} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarImage src={post.user_avatar} />
                        <AvatarFallback>{post.user_name?.[0] || 'U'}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold">{post.user_name}</p>
                        <p className="text-sm text-gray-500">{formatDate(post.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={typeInfo.color}>
                        <typeInfo.icon className="h-3 w-3 mr-1" />
                        {typeInfo.label}
                      </Badge>
                      {post.user_id === currentUserId && (
                        <button onClick={() => handleDelete(post.id)} className="text-gray-400 hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap mb-3">{post.content}</p>
                  {post.image_url && (
                    <img src={post.image_url} alt="" className="rounded-lg max-h-96 w-full object-cover mb-3" />
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-4 pt-3 border-t">
                    <button
                      onClick={() => handleLike(post.id)}
                      className={`flex items-center gap-1 ${isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
                    >
                      <Heart className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
                      <span>{post.like_count || 0}</span>
                    </button>
                    <button
                      onClick={() => setShowComments({ ...showComments, [post.id]: !showComments[post.id] })}
                      className="flex items-center gap-1 text-gray-500 hover:text-purple-500"
                    >
                      <MessageCircle className="h-5 w-5" />
                      <span>{post.comment_count || 0}</span>
                    </button>
                  </div>

                  {/* Comments */}
                  {showComments[post.id] && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      {(post.comments || []).map(comment => (
                        <div key={comment.id} className="flex gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{comment.user_name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 bg-gray-50 rounded-lg p-2">
                            <p className="text-sm font-medium">{comment.user_name}</p>
                            <p className="text-sm">{comment.content}</p>
                          </div>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={commentText[post.id] || ''}
                          onChange={(e) => setCommentText({ ...commentText, [post.id]: e.target.value })}
                          placeholder="Yorum yaz..."
                          className="flex-1 px-3 py-2 border rounded-lg text-sm"
                          onKeyPress={(e) => e.key === 'Enter' && handleComment(post.id)}
                        />
                        <Button size="sm" onClick={() => handleComment(post.id)}>
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
